import {useEffect, useState, useSyncExternalStore} from "react";
import {useRecoilValue} from "recoil";
import {useLocation} from "react-router-dom";
import {globalTwoFactorActionState, globalUserVerifyState} from "../global_state";
import {getVersion, isDialogHeld, subscribe} from "../dialogGate";
import {isPasskeyPromptPending} from "../passkeyPromptState";
import {isPromptSkipPath} from "../promptSkipPaths";

// the page settles first: an offer that lands with the dashboard reads as a pop-up ad
export const INSTALL_OFFER_DELAY_MS = 4000;

// a login marks the passkey prompt, but a demo account never consumes the mark; past this the
// mark is stale for the tab and must not keep the install offer away for good
export const PASSKEY_PENDING_GRACE_MS = 20000;

/**
 * True once the install offer may open on its own: logged in, off the auth and verification
 * paths, nothing else holding the dialog gate (passkey prompt deciding or open, a two-factor
 * challenge), and all of that for INSTALL_OFFER_DELAY_MS without a break.
 */
const useInstallOfferGate = (wanted) => {
    const location = useLocation();
    const verifyState = useRecoilValue(globalUserVerifyState);
    const twoFactorChallenge = useRecoilValue(globalTwoFactorActionState);

    useSyncExternalStore(subscribe, getVersion);

    const [ready, setReady] = useState(false);

    const eligible = !!wanted && !!verifyState && !isPromptSkipPath(location.pathname);
    const blocked = isDialogHeld() || !!twoFactorChallenge;
    const passkeyPending = isPasskeyPromptPending();

    // the grace runs from when the passkey prompt became owed (the login), not from page load:
    // time spent on the login page must not use it up
    const [pendingSince, setPendingSince] = useState(() => (passkeyPending ? Date.now() : null));

    useEffect(() => {
        setPendingSince((since) => (passkeyPending ? since ?? Date.now() : null));
    }, [passkeyPending]);

    useEffect(() => {
        setReady(false);

        if (!eligible || blocked) return;

        // a pending passkey prompt is waited on, but only for the rest of the grace
        const graceLeft =
            passkeyPending && pendingSince ? Math.max(pendingSince + PASSKEY_PENDING_GRACE_MS - Date.now(), 0) : 0;
        const timer = setTimeout(() => setReady(true), Math.max(INSTALL_OFFER_DELAY_MS, graceLeft));

        return () => clearTimeout(timer);
    }, [eligible, blocked, passkeyPending, pendingSince, location.pathname]);

    return ready && eligible && !blocked;
};

export default useInstallOfferGate;
